import { VideoJsPlayer, VideoJsPlayerOptions } from 'video.js';

import { defaultVideoTrackKind, defaultVideoTrackLanguage } from '@/constants/video';
import { OriginalsEpisodeTranscripts } from '@/resources/OriginalsEpisodeTranscripts';
import { ThinEvent } from '@/resources/ThinEvent';
import { ThinLecture } from '@/resources/ThinLecture';
import { Transcript } from '@/resources/Transcript';
import { ILibLynxMetadata } from '@/types/LibLynx';

const isDefaultTranscriptLanguage = (transcript: Transcript | OriginalsEpisodeTranscripts) =>
  transcript.language.locale === defaultVideoTrackLanguage && !transcript.autoGenerated;

const isManualTranscriptLanguage = (transcript: Transcript) => !transcript.autoGenerated;

interface ITrackOptionsConfig {
  isInitial: boolean;
  autoCanDefault: boolean;
}

export const getVideoAnalyticsAttributes = (lecture: ThinLecture, event: ThinEvent) => ({
  lecture_id: lecture?.id,
  lecture_name: lecture?.title,
  event_id: event?.id,
  event_name: event?.name,
  speaker_name: lecture?.sortedProfiles?.map((speaker) => {
    return speaker?.fullName;
  }),
});

export const getLibLynxMetadata = (lecture: ThinLecture) =>
  ({
    dtype: 'multimedia',
    id: lecture?.id,
    title: lecture?.title,
  } as ILibLynxMetadata);

export const getTrackOptions = (
  transcripts: Array<Transcript | OriginalsEpisodeTranscripts>,
  activeTrackId: string,
  options: ITrackOptionsConfig
) => {
  const { isInitial, autoCanDefault } = options;

  const isDefaultPresent = Boolean(transcripts?.find(isDefaultTranscriptLanguage));
  const isManualPresent = Boolean(transcripts?.find(isManualTranscriptLanguage));

  const trackTranscripts = transcripts?.map((transcript) => {
    const isDefault = isInitial ? isDefaultTranscriptLanguage(transcript) : activeTrackId === transcript.id;

    const trackOptions = {
      id: transcript.id,
      kind: defaultVideoTrackKind,
      src: transcript.subtitleUrl,
      label: transcript.language.name,
      default: isDefault,
      srclang: transcript.language.locale,
      mode: isDefault ? 'showing' : 'disabled',
    };

    if (isInitial) {
      // if there is no default, pick any non-automatic
      if (!isDefaultPresent && !transcript.autoGenerated) {
        trackOptions.default = true;
        trackOptions.mode = 'showing';
        return trackOptions;
      }
      // else take any
      if (!isManualPresent && autoCanDefault) {
        trackOptions.default = true;
        trackOptions.mode = 'showing';
      }
    }

    return trackOptions;
  });

  if (trackTranscripts) {
    const hasTrackShowing = trackTranscripts.some((tt) => tt.mode === 'showing');

    if (!hasTrackShowing && trackTranscripts.length > 0) {
      trackTranscripts[0].mode = 'showing';
    }
  }

  return trackTranscripts as VideoJsPlayerOptions['tracks'];
};

export const getActiveTrack = (player: VideoJsPlayer): TextTrack => {
  if (!player) return null;
  const textTracks = player.textTracks();

  for (let i = 0; i < textTracks.length; i++) {
    if (textTracks[i].mode === 'showing') {
      return textTracks[i];
    }
  }
  return null;
};

export const setActiveTrack = (player: VideoJsPlayer, trackId: string) => {
  const tracks = player.textTracks();

  for (let i = 0; i < tracks.length; i++) {
    const track = tracks[i];

    if (track.id === trackId) {
      track.mode = 'showing';
    } else {
      track.mode = 'disabled';
    }
  }
};

export const setHideTrack = (player: VideoJsPlayer) => {
  const tracks = player.textTracks();

  for (let i = 0; i < tracks.length; i++) {
    const track = tracks[i];

    if (track.mode === 'showing') {
      track.mode = 'hidden';
    }
  }
};
